import React, { useState, useCallback, useEffect, ReactElement } from 'react';
import { Typography, Box } from '@mui/material';
import { AlyceLoading } from '@alycecom/ui';

import UnsubscribeLayout from '../../components/UnsubscribeLayout/UnsubscribeLayout';
import DeclineGiftInviteStep from '../../components/DeclineGiftInviteStep/DeclineGiftInviteStep';
import DeclineSuccessStep from '../../components/DeclineSuccessStep/DeclineSuccessStep';
import UnsubscribeFromCompany from '../../components/UnsubscribeFromCompany/UnsubscribeFromCompany';
import ReSubscribeLink from '../../components/ReSubscribeLink/ReSubscribeLink';
import unsubscribeService, { emptyGift } from '../../services/unsubscribe.service';
import MessageSnackbar from '../../components/MessageSnackbar/MessageSnackbar';
import { useGlobalMessage } from '../../hooks/useGlobalMessage';
import { analyticService } from '../../services/analytics.service';
import { IGift } from '../../types/gift';

const Unsubscribe = (): ReactElement => {
  const initPageTitle = 'Unsubscribe options.';

  const [pageTitle, setPageTitle] = useState(initPageTitle);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [gift, setGift] = useState<IGift>(emptyGift);
  const [messageState, showMessage, hideMessage] = useGlobalMessage();

  const declineSubmitHandler = useCallback(
    (declineType, feedback) => {
      setIsLoading(true);
      unsubscribeService.unsubscribe(declineType, feedback).then(
        loadGift => {
          setGift(loadGift);
          setIsLoading(false);
          setPageTitle('You have successfully unsubscribed from the option you selected!');
        },
        declineErrors => {
          setIsLoading(false);
          if (declineErrors.global) {
            showMessage(declineErrors.global, 'error');
          } else {
            setErrors(declineErrors);
          }
        },
      );
    },
    [setPageTitle, setIsLoading, setErrors, showMessage],
  );

  const reSubscribeHandler = useCallback(() => {
    setIsLoading(true);
    unsubscribeService.cancel().then(
      loadGift => {
        setGift(loadGift);
        setPageTitle(initPageTitle);
        setErrors({});
        setIsLoading(false);
        showMessage('Thank you for re-subscribing to Alyce!');
      },
      globalError => {
        setIsLoading(false);
        showMessage(globalError.global, 'error');
      },
    );
  }, [setIsLoading, setGift, setErrors, showMessage, setPageTitle]);

  const onCompanyUnsubscribeHandler = useCallback(() => {
    analyticService.trackEvent({ name: 'Recipient Company Unsubscribe', payload: gift });
  }, [gift]);

  const clearErrorsHandler = useCallback(() => {
    setErrors({});
  }, [setErrors]);

  useEffect(() => {
    unsubscribeService.getCommonData().then(
      loadGift => {
        setGift(loadGift);
        setIsInitLoading(false);

        if (loadGift.isFeedbackSent) {
          setPageTitle('You have successfully unsubscribed from the option you selected!');
        }

        const { recipient } = loadGift;
        analyticService.setIdentity(recipient.id, { contactExternalId: recipient.email }, {});
      },
      globalError => {
        if (globalError.global) {
          showMessage(globalError.global, 'error');
        }
      },
    );
  }, [showMessage]);

  const margins = { xs: 1, sm: 4, lg: 7 };

  const isDeclineStep = !gift.isFeedbackSent;
  const showResubscribeLink = gift.isUnsubscribed;
  const showCompanySection = gift.company && gift.outerUnsubscribeUrl;
  const { message, variant, isOpened } = messageState;

  return (
    <UnsubscribeLayout>
      <AlyceLoading isLoading={isInitLoading}>
        <Box mt={4} pl={margins} pr={margins}>
          <Typography className="H2-Chambray" data-testid="unsubscribePageTitle">
            {pageTitle}
          </Typography>
          {isDeclineStep ? (
            <DeclineGiftInviteStep
              gift={gift}
              isLoading={isLoading}
              errors={errors}
              onSubmit={declineSubmitHandler}
              onChangeFeedback={clearErrorsHandler}
            />
          ) : (
            <DeclineSuccessStep gift={gift} />
          )}
          {showResubscribeLink && <ReSubscribeLink onClick={reSubscribeHandler} />}
          {showCompanySection && (
            <UnsubscribeFromCompany
              company={gift.company}
              unsubscribeLink={gift.outerUnsubscribeUrl}
              onLinkClick={onCompanyUnsubscribeHandler}
            />
          )}
        </Box>
      </AlyceLoading>
      <MessageSnackbar isOpened={isOpened} message={message} variant={variant} onClose={hideMessage} />
    </UnsubscribeLayout>
  );
};

export default Unsubscribe;
